import { useMutation } from '@tanstack/react-query'

import { checkCustomerDuplicate } from '@/api/customers-api'

export type DuplicateField = 'ghanaCardNumber' | 'phoneNumber'

export type DuplicateStatus = 'idle' | 'checking' | 'clear' | 'duplicate' | 'unavailable'

export interface DuplicateCheck {
  /** Asks the question. Called when a field is left, never on every keystroke. */
  check: (value: string) => void
  /**
   * The answer, but only if it is an answer about the value currently in the field. Editing after
   * a check silently returns the field to `idle` rather than leaving a verdict about a number the
   * operator has since changed.
   */
  statusFor: (value: string) => DuplicateStatus
  existingCustomerId: string | null
}

/**
 * The onboarding duplicate check for one field.
 *
 * It is a mutation rather than a query because it is a question asked at a moment the operator
 * chose — leaving the field — and because its answer is never cached: the customer base changes
 * all day, and a remembered "no duplicate" is the wrong answer to keep.
 *
 * The result never blocks typing. A warning is shown, the operator decides, and the server checks
 * again on submission against a blind index this browser cannot reproduce.
 */
export function useDuplicateCheck(field: DuplicateField): DuplicateCheck {
  const mutation = useMutation({
    mutationFn: (value: string) =>
      checkCustomerDuplicate(field === 'ghanaCardNumber' ? { ghanaCardNumber: value } : { phoneNumber: value }),
  })

  const check = (value: string) => {
    const trimmed = value.trim()
    if (trimmed === '' || trimmed === mutation.variables) {
      return
    }

    mutation.mutate(trimmed)
  }

  const statusFor = (value: string): DuplicateStatus => {
    if (mutation.variables !== value.trim()) {
      return 'idle'
    }

    if (mutation.isPending) {
      return 'checking'
    }

    if (mutation.isError) {
      // A duplicate check that could not be made is reported as exactly that. Reporting it as
      // "clear" would be a claim the client is in no position to make.
      return 'unavailable'
    }

    if (!mutation.data) {
      return 'idle'
    }

    return mutation.data.isDuplicate ? 'duplicate' : 'clear'
  }

  return { check, statusFor, existingCustomerId: mutation.data?.existingCustomerId ?? null }
}
